import React, {useMemo} from 'react'
import { AsideMenuItemWithSub } from './AsideMenuItemWithSub'
import { AsideMenuItem } from './AsideMenuItem'
import routes from './routes.json'
import { useProducts } from '../../../../app/contexts/ProductsProvider'
import {useUser} from '../../../../app/modules/context/types'

interface MenuConfig {
    title: string;
    icon: string;
    createUrl: string;
    activeUrl: string;
}

interface MenuRoute {
    primary: string;
    secondary?: { to: string; title: string }[];
}

interface Routes {
    CallTracking: MenuRoute;
    CallTrackingStatistics: MenuRoute;
    WebForms: MenuRoute;
    Mailing: MenuRoute;
    Reviews: MenuRoute;
    Statistics: MenuRoute;
    Contacts: MenuRoute;
    Report: MenuRoute;
    UpdatePlugins: MenuRoute;
    Sav: MenuRoute;
    [key: string]: MenuRoute;
}

interface AccountStatuses {
    CallTracking: boolean;
    Statistics: boolean;
    WebForms: boolean;
    Mailing: boolean;
    UpdatePlugins: boolean;
    Sav: boolean;
    Reviews: boolean;
}

const menuConfig: { [key: string]: MenuConfig } = {
    CallTracking: {
        title: "Journal des appels",
        icon: "/media/icons/duotune/communication/com014.svg",
        createUrl: "/creer-compte-calltracking",
        activeUrl: "/call-tracking/journal-appels"
    },
    WebForms: {
        title: "Vos formulaires",
        icon: "/media/icons/duotune/general/gen005.svg",
        createUrl: "/creer-compte-webformulaires",
        activeUrl: "/web-formulaires/tableau-de-bord-wf"
    },
    Mailing: {
        title: "Vos campagnes e-mails",
        icon: "/media/icons/duotune/communication/com002.svg",
        createUrl: "/creer-compte-mailing",
        activeUrl: "/mailing"
    },
    Statistics: {
        title: "Référencement naturel",
        icon: "/media/icons/duotune/general/gen032.svg",
        createUrl: "/creer-compte-statistiques",
        activeUrl: "/statistiques/s-seo"
    },
    UpdatePlugins: {
        title: "Mises à jour du site web",
        icon: "/media/icons/duotune/arrows/arr031.svg",
        createUrl: "/creer-compte-mise-a-jours",
        activeUrl: "/mise-a-jours"
    },
    Sav: {
        title: "Support",
        icon: "/media/icons/duotune/files/fil003.svg",
        createUrl: "/creer-compte-sav",
        activeUrl: "/sav"
    },
    Reviews: {
        title: "Mes avis Google",
        icon: "/media/icons/duotune/social/soc011.svg",
        createUrl: "/creer-compte-reviews",
        activeUrl: "/reviews"
    },
}

const checkProductStatus = (
    productsActive: Record<string, {activated_at: string | null}>,
    productName: string
): boolean => {
    return !!productsActive[productName]?.activated_at
}

export function AsideMenuMain() {
    const {productsActive} = useProducts()
    const {personalInformation: {googleAnalytics}} = useUser()

    const accountStatuses = useMemo<AccountStatuses>(
        () => ({
            CallTracking: checkProductStatus(productsActive, 'Calltraking'),
            Statistics: checkProductStatus(productsActive, 'Statistiques'),
            WebForms: checkProductStatus(productsActive, 'Webformulaires'),
            Mailing: checkProductStatus(productsActive, 'Mailing'),
            UpdatePlugins: checkProductStatus(productsActive, 'UpdatePlugins'),
            Sav: checkProductStatus(productsActive, 'Sav'),
            Reviews: checkProductStatus(productsActive, 'Reviews'),
        }),
        [productsActive]
    )

    return (
        <>
            <AsideMenuItem
                to="/dashboard"
                icon="/media/icons/duotune/art/art002.svg"
                title="Tableau de bord"
                fontIcon="bi-app-indicator"
            />

            {Object.entries(menuConfig).map(([key, config]) => {

                const route = (routes as Partial<Routes>)[key];
                const isActive = accountStatuses[key as keyof AccountStatuses];

                return isActive ? (
                    key === 'Mailing' ? (
                        <AsideMenuItemWithSub
                            key={key}
                            to={route?.primary || config.activeUrl}
                            title={config.title}
                            icon={config.icon}
                            fontIcon="bi-layers"
                        >
                            {route?.secondary?.map((item, index) => (
                                <AsideMenuItem
                                    to={`${route?.primary}${item.to}`}
                                    title={item.title}
                                    hasBullet={true}
                                    key={item.to || index}
                                />
                            ))}
                        </AsideMenuItemWithSub>
                    ) : (
                        <AsideMenuItem
                            key={key}
                            to={config.activeUrl}
                            icon={config.icon}
                            title={config.title}
                            fontIcon="bi-layers"
                        />
                    )
                ) : (
                    <AsideMenuItem
                        key={key}
                        to={config.createUrl}
                        icon={config.icon}
                        title={config.title}
                        fontIcon="bi-layers"
                    />
                )
            })}

            <AsideMenuItem
                to={routes.Contacts.primary}
                icon="/media/icons/duotune/communication/com013.svg"
                title="Contacts"
                fontIcon="bi-layers"
            />

            <AsideMenuItem
                to={"/clicks"}
                icon="/media/icons/duotune/coding/cod006.svg"
                title="Conversion Clics"
                fontIcon="bi-layers"
            />

            {googleAnalytics && (
                <AsideMenuItem
                    to={routes.Report.primary}
                    icon="/media/icons/duotune/general/gen062.svg"
                    title="Rapport"
                    fontIcon="bi-layers"
                />
            )}
        </>
    )
}
